/// <reference path="../../polaris-web-components.d.ts" />
import React, { useState, useEffect, useCallback } from 'react';
import { FormatTimeDuration } from '../common/FormatTimeDuration';
import CustomProgressBar from './CustomProgressBar';

export default function ImportHistory({ logs, generateProductErrorModal, downloadResult, loadingMap, generateMetafieldErrorModal, generateUploadWarningsModal, generateCombinedErrorModal, logsLoading, isCustomerImport, hasPreviousPage, hasNextPage, onPreviousPage, onNextPage, heading, subheading, hideHeader }) {
    const resourceName = {
        singular: 'log',
        plural: 'logs'
    };

    const [spinningErrorKey, setSpinningErrorKey] = useState(null);

    const attachModalCloseListeners = useCallback(() => {
        const modalIds = ['bulk-error-modal', 'bulk-meta-error-modal', 'upload-warnings-modal', 'combined-error-modal'];
        modalIds.forEach((id) => {
            const modal = document.getElementById(id);
            if (modal && !modal._closeListenerAttached) {
                const handler = (e) => {
                    if (e.newState === 'closed' || e.type === 'hide' || e.type === 's-hide') {
                        setSpinningErrorKey(null);
                    }
                };
                modal.addEventListener('toggle', handler);
                modal.addEventListener('hide', handler);
                modal.addEventListener('s-hide', handler);
                modal._closeListenerAttached = true;
            }
        });
    }, []);

    const handleErrorClick = useCallback((key, fn) => {
        setSpinningErrorKey(key);
        attachModalCloseListeners();
        fn();
    }, [attachModalCloseListeners]);

    const ErrorLink = ({ errorKey, count, onClick, tone = "critical" }) => (
        <s-stack direction="inline" gap="tight" alignItems="center">
            {spinningErrorKey === errorKey
                ? <s-spinner size="small" accessibilityLabel="Loading errors" />
                : null}
            <s-link tone={tone} onClick={onClick}>
                <s-text tone={tone}>{count} errors</s-text>
            </s-link>
        </s-stack>
    );

    const WarningLink = ({ errorKey, count, onClick }) => (
        <s-stack direction="inline" gap="tight" alignItems="center">
            {spinningErrorKey === errorKey
                ? <s-spinner size="small" accessibilityLabel="Loading warnings" />
                : null}
            <s-link tone="warning" onClick={onClick}>
                <s-text tone="warning">{count} warnings</s-text>
            </s-link>
        </s-stack>
    );

    return (
        <>
            {!hideHeader && (
                <s-box padding="base">
                    <s-grid gridTemplateColumns="1fr auto" gap="none">
                        <s-grid-item>
                            <s-heading>{heading || "Import History"}</s-heading>
                        </s-grid-item>
                        <s-grid-item>
                            <s-text tone="subdued">{subheading || "Imports from last 7 days"}</s-text>
                        </s-grid-item>
                    </s-grid>
                </s-box>
            )}
            <s-table
                loading={logsLoading}
                paginate={logs.length > 0}
                hasPreviousPage={hasPreviousPage}
                hasNextPage={hasNextPage}
                onPreviousPage={onPreviousPage}
                onNextPage={onNextPage}
            >
                <s-table-header-row>
                    <s-table-header>Id</s-table-header>
                    <s-table-header>Connection</s-table-header>
                    <s-table-header>Type</s-table-header>
                    <s-table-header>Created at</s-table-header>
                    <s-table-header>Items</s-table-header>
                    <s-table-header>Duration</s-table-header>
                    <s-table-header>Errors</s-table-header>
                    <s-table-header>Warnings</s-table-header>
                    <s-table-header>Status</s-table-header>
                </s-table-header-row>
                <s-table-body>
                    {logsLoading ? (
                        <s-table-row>
                            <s-table-cell></s-table-cell>
                            <s-table-cell></s-table-cell>
                            <s-table-cell></s-table-cell>
                            <s-table-cell></s-table-cell>
                            <s-table-cell>
                                <s-stack alignItems="center" gap="base" padding="large">
                                    <s-spinner accessibilityLabel="Loading import history" size="large" />
                                </s-stack>
                            </s-table-cell>
                        </s-table-row>
                    ) : logs.length === 0 ? (
                        <s-table-row>
                            <s-table-cell colSpan="9">
                                <s-box padding="large">
                                    <s-stack alignment="center">
                                        <s-text>No logs yet</s-text>
                                    </s-stack>
                                </s-box>
                            </s-table-cell>
                        </s-table-row>
                    ) :
                        logs.map(
                            ({ id, importType, bulkId, update, variantUpdate, variantCreate, mediaUpdate, mediaCreate, create, publish, unpublish, metaUpdate, startTime, endTime, status, catalogUrl, metaDelete, productDelete, uploadErrors }, index) => (
                                <>
                                    {update ?
                                        <s-table-row
                                            id={bulkId}
                                            position={index}
                                            key={bulkId + "update"}
                                        >
                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    <s-text type="strong">{'#' + id}</s-text>
                                                    {catalogUrl ? (
                                                        <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                            <s-icon type="upload" tone='base' />
                                                        </s-link>
                                                    ) : null}
                                                </s-stack>
                                            </s-table-cell>
                                            <s-table-cell> <s-text type="strong">{importType}</s-text>
                                            </s-table-cell>
                                            <s-table-cell><s-text type="strong">Update</s-text></s-table-cell>
                                            <s-table-cell>{update?.timeAgo}</s-table-cell>
                                            <s-table-cell>{update?.total}</s-table-cell>
                                            <s-table-cell>{update?.endTime ? FormatTimeDuration(update?.startTime, update?.endTime) : "_"}</s-table-cell>
                                            <s-table-cell>{((update?.errors && update?.errors?.length > 0) || (isCustomerImport && update?.metaErrors && update?.metaErrors?.length > 0)) ?
                                                <ErrorLink
                                                    errorKey={bulkId + "-update-err"}
                                                    count={isCustomerImport ? (update?.errors?.length || 0) + (update?.metaErrors?.length || 0) : update?.errors?.length}
                                                    onClick={() => handleErrorClick(bulkId + "-update-err", () => {
                                                        if (isCustomerImport) {
                                                            generateCombinedErrorModal(update?.errors || [], update?.metaErrors || []);
                                                        } else {
                                                            console.log("Product errors:", update?.errors);
                                                            generateProductErrorModal(update?.errors);
                                                        }
                                                    })}
                                                />
                                                : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>}


                                            </s-table-cell>
                                            <s-table-cell>
                                                {(uploadErrors?.length > 0) ?
                                                    <WarningLink
                                                        errorKey={bulkId + "-update-warn"}
                                                        count={uploadErrors?.length}
                                                        onClick={() => handleErrorClick(bulkId + "-update-warn", () => generateUploadWarningsModal(uploadErrors))}
                                                    />
                                                    : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>}
                                            </s-table-cell>

                                            <s-table-cell>
                                                {status !== "canceled" ?
                                                    <s-badge tone={update?.status === "completed" ? "success" : "info"}>{update?.status}</s-badge>
                                                    : <s-badge tone="critical">{status}</s-badge>
                                                }
                                            </s-table-cell>
                                        </s-table-row> : null}




                                    {create ?
                                        <s-table-row key={bulkId + "create"}>

                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    <s-text type="strong">{update ? "" : '#' + id}</s-text>
                                                    {catalogUrl && !update ? (
                                                        <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                            <s-icon type="upload" tone='base' />
                                                        </s-link>
                                                    ) : null}
                                                </s-stack>
                                            </s-table-cell>
                                            <s-table-cell> {!update ? (<s-text type="strong">{importType}</s-text>) : null} </s-table-cell>
                                            <s-table-cell><s-text type="strong">Create</s-text></s-table-cell>
                                            <s-table-cell>{create.startTime ? create?.timeAgo : update?.timeAgo}</s-table-cell>
                                            <s-table-cell>{create.total}</s-table-cell>
                                            {/* <s-table-cell><div style={{ width: '125px' }}>
                                        <ProgressBar progress={create.completed != 0 ? (create.completed / create.total) * 100 : 0} size="medium" />
                                    </div></s-table-cell> */}
                                            <s-table-cell>{create?.endTime ? FormatTimeDuration(create?.startTime, create?.endTime) : "_"}</s-table-cell>
                                            <s-table-cell>{((create?.errors && create?.errors?.length > 0) || (isCustomerImport && create?.metaErrors && create?.metaErrors?.length > 0)) ?
                                                <ErrorLink
                                                    errorKey={bulkId + "-create-err"}
                                                    count={isCustomerImport ? (create?.errors?.length || 0) + (create?.metaErrors?.length || 0) : create?.errors?.length}
                                                    onClick={() => handleErrorClick(bulkId + "-create-err", () => {
                                                        if (isCustomerImport) {
                                                            console.log("Customer errors:", create?.errors, "Metafield errors:", create?.metaErrors);
                                                            generateCombinedErrorModal(create?.errors || [], create?.metaErrors || []);
                                                        } else {
                                                            console.log("Product errors:", create?.errors);
                                                            generateProductErrorModal(create?.errors);
                                                        }
                                                    })}
                                                />
                                                : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>
                                            }

                                            </s-table-cell>
                                            <s-table-cell>
                                                {(uploadErrors?.length > 0) ?
                                                    <WarningLink
                                                        errorKey={bulkId + "-create-warn"}
                                                        count={uploadErrors?.length}
                                                        onClick={() => handleErrorClick(bulkId + "-create-warn", () => generateUploadWarningsModal(uploadErrors))}
                                                    />
                                                    : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>}
                                            </s-table-cell>
                                            <s-table-cell>
                                                {status !== "canceled" ?
                                                    <s-badge tone={create?.status === "completed" ? "success" : "info"}>{create?.status}</s-badge>
                                                    : <s-badge tone="critical">{status}</s-badge>
                                                }
                                            </s-table-cell>
                                        </s-table-row> : null}



                                    {variantUpdate ?
                                        <s-table-row
                                            id={bulkId}
                                            position={index}
                                            key={bulkId + "variantUpdate"}
                                        >
                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    {catalogUrl && !update && !create ? (
                                                        <>
                                                            <s-text type="strong">{'#' + id}</s-text>
                                                            <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                                <s-icon type="upload" tone='base' />
                                                            </s-link>
                                                        </>
                                                    ) : null}
                                                </s-stack>                                    </s-table-cell>
                                            <s-table-cell> {!update && !create ? (<s-text type="strong">{importType}</s-text>) : null} </s-table-cell>
                                            <s-table-cell><s-text type="strong">Inventory update</s-text></s-table-cell>
                                            <s-table-cell>{variantUpdate?.timeAgo}</s-table-cell>
                                            <s-table-cell>{variantUpdate?.total}</s-table-cell>
                                            <s-table-cell>{variantUpdate?.endTime ? FormatTimeDuration(variantUpdate?.startTime, variantUpdate?.endTime) : "_"}</s-table-cell>
                                            <s-table-cell>{(variantUpdate?.errors && variantUpdate?.errors?.length > 0) ?
                                                <ErrorLink
                                                    errorKey={bulkId + "-variantUpdate-err"}
                                                    count={variantUpdate?.errors?.length}
                                                    onClick={() => handleErrorClick(bulkId + "-variantUpdate-err", () => generateProductErrorModal(variantUpdate?.errors))}
                                                />
                                                : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>}
                                            </s-table-cell>
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>
                                                {status !== "canceled" ?
                                                    <s-badge tone={variantUpdate?.status === "completed" ? "success" : "info"}>{variantUpdate?.status}</s-badge>
                                                    : <s-badge tone="critical">{status}</s-badge>
                                                }
                                            </s-table-cell>
                                        </s-table-row> : null}


                                    {variantCreate ?
                                        <s-table-row
                                            id={bulkId}
                                            position={index}
                                            key={bulkId + "variantCreate"}
                                        >
                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    {catalogUrl && !update && !create ? (
                                                        <>
                                                            <s-text type="strong">{'#' + id}</s-text>
                                                            <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                                <s-icon type="upload" tone='base' />
                                                            </s-link>
                                                        </>
                                                    ) : null}
                                                </s-stack>                                    </s-table-cell>
                                            <s-table-cell> {!update && !create ? (<s-text type="strong">{importType}</s-text>) : null} </s-table-cell>
                                            <s-table-cell><s-text type="strong">Variant create</s-text></s-table-cell>
                                            <s-table-cell>{variantCreate?.timeAgo}</s-table-cell>
                                            <s-table-cell>{variantCreate?.total}</s-table-cell>
                                            <s-table-cell>{variantCreate?.endTime ? FormatTimeDuration(variantCreate?.startTime, variantCreate?.endTime) : "_"}</s-table-cell>
                                            <s-table-cell>{(variantCreate?.errors && variantCreate?.errors?.length > 0) ?
                                                <ErrorLink
                                                    errorKey={bulkId + "-variantCreate-err"}
                                                    count={variantCreate?.errors?.length}
                                                    onClick={() => handleErrorClick(bulkId + "-variantCreate-err", () => generateProductErrorModal(variantCreate?.errors))}
                                                />
                                                : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>}
                                            </s-table-cell>
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>
                                                {status !== "canceled" ?
                                                    <s-badge tone={variantCreate?.status === "completed" ? "success" : "info"}>{variantCreate?.status}</s-badge>
                                                    : <s-badge tone="critical">{status}</s-badge>
                                                }
                                            </s-table-cell>
                                        </s-table-row> : null}



                                    {mediaUpdate ?
                                        <s-table-row
                                            id={bulkId}
                                            position={index}
                                            key={bulkId + "mediaUpdate"}
                                        >
                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    {catalogUrl && !update && !create ? (
                                                        <>
                                                            <s-text type="strong">{'#' + id}</s-text>
                                                            <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                                <s-icon type="upload" tone='base' />
                                                            </s-link>
                                                        </>
                                                    ) : null}
                                                </s-stack>                                    </s-table-cell>
                                            <s-table-cell> {!update && !create ? (<s-text type="strong">{importType}</s-text>) : null} </s-table-cell>
                                            <s-table-cell><s-text type="strong">Media update</s-text></s-table-cell>
                                            <s-table-cell>{mediaUpdate?.timeAgo}</s-table-cell>
                                            <s-table-cell>{mediaUpdate?.total}</s-table-cell>
                                            <s-table-cell>{mediaUpdate?.endTime ? FormatTimeDuration(mediaUpdate?.startTime, mediaUpdate?.endTime) : "_"}</s-table-cell>
                                            <s-table-cell>{(mediaUpdate?.errors && mediaUpdate?.errors?.length > 0) ?
                                                <ErrorLink
                                                    errorKey={bulkId + "-mediaUpdate-err"}
                                                    count={mediaUpdate?.errors?.length}
                                                    onClick={() => handleErrorClick(bulkId + "-mediaUpdate-err", () => generateProductErrorModal(mediaUpdate?.errors))}
                                                />
                                                : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>}
                                            </s-table-cell>
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>
                                                {status !== "canceled" ?
                                                    <s-badge tone={mediaUpdate?.status === "completed" ? "success" : "info"}>{mediaUpdate?.status}</s-badge>
                                                    : <s-badge tone="critical">{status}</s-badge>
                                                }
                                            </s-table-cell>
                                        </s-table-row> : null}


                                    {mediaCreate ?
                                        <s-table-row
                                            id={bulkId}
                                            position={index}
                                            key={bulkId + "mediaCreate"}
                                        >
                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    {catalogUrl && !update && !create ? (
                                                        <>
                                                            <s-text type="strong">{'#' + id}</s-text>

                                                            <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                                <s-icon type="upload" tone='base' />
                                                            </s-link>
                                                        </>
                                                    ) : null}
                                                </s-stack>                                    </s-table-cell>
                                            <s-table-cell> {!update && !create ? (<s-text type="strong">{importType}</s-text>) : null} </s-table-cell>
                                            <s-table-cell><s-text type="strong">Media create</s-text></s-table-cell>
                                            <s-table-cell>{mediaCreate?.timeAgo}</s-table-cell>
                                            <s-table-cell>{mediaCreate?.total}</s-table-cell>
                                            <s-table-cell>{mediaCreate?.endTime ? FormatTimeDuration(mediaCreate?.startTime, mediaCreate?.endTime) : "_"}</s-table-cell>
                                            <s-table-cell>{(mediaCreate?.errors && mediaCreate?.errors?.length > 0) ?
                                                <ErrorLink
                                                    errorKey={bulkId + "-mediaCreate-err"}
                                                    count={mediaCreate?.errors?.length}
                                                    onClick={() => handleErrorClick(bulkId + "-mediaCreate-err", () => generateProductErrorModal(mediaCreate?.errors))}
                                                />
                                                : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>}
                                            </s-table-cell>
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>
                                                {status !== "canceled" ?
                                                    <s-badge tone={mediaCreate?.status === "completed" ? "success" : "info"}>{mediaCreate?.status}</s-badge>
                                                    : <s-badge tone="critical">{status}</s-badge>
                                                }
                                            </s-table-cell>
                                        </s-table-row> : null}




                                    {publish ?
                                        <s-table-row key={bulkId + "publish"}>

                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    <s-text type="strong">{publish ? "" : '#' + id}</s-text>
                                                    {catalogUrl && !publish ? (
                                                        <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                            <s-icon type="upload" tone='base' />
                                                        </s-link>
                                                    ) : null}
                                                </s-stack>                                    </s-table-cell>
                                            <s-table-cell> {!update && !create ? (<s-text type="strong">{importType}</s-text>) : null} </s-table-cell>
                                            <s-table-cell><s-text type="strong">Publish</s-text></s-table-cell>
                                            <s-table-cell>{publish?.startTime ? publish?.timeAgo : update?.timeAgo}</s-table-cell>
                                            <s-table-cell>{publish?.total}</s-table-cell>
                                            {/* <s-table-cell><div style={{ width: '125px' }}>
                                        <ProgressBar progress={create.completed != 0 ? (create.completed / create.total) * 100 : 0} size="medium" />
                                    </div></s-table-cell> */}
                                            <s-table-cell>{publish?.endTime ? FormatTimeDuration(publish?.startTime, publish?.endTime) : "_"}</s-table-cell>
                                            <s-table-cell>{(publish?.errors && publish?.errors?.length > 0) ?
                                                <ErrorLink
                                                    errorKey={bulkId + "-publish-err"}
                                                    count={publish?.errors?.length}
                                                    onClick={() => handleErrorClick(bulkId + "-publish-err", () => generateProductErrorModal(publish?.errors))}
                                                />
                                                : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>}
                                            </s-table-cell>
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>
                                                {status !== "canceled" ?
                                                    <s-badge tone={publish?.status === "completed" ? "success" : "info"}>{publish?.status}</s-badge>
                                                    : <s-badge tone="critical">{status}</s-badge>
                                                }
                                            </s-table-cell>
                                        </s-table-row> : null}




                                    {unpublish ?
                                        <s-table-row key={bulkId + "unpublish"}>

                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    <s-text type="strong">{unpublish ? "" : '#' + id}</s-text>
                                                    {catalogUrl && !unpublish ? (
                                                        <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                            <s-icon type="upload" tone='base' />
                                                        </s-link>
                                                    ) : null}
                                                </s-stack>                                    </s-table-cell>
                                            <s-table-cell> {!update && !create ? (<s-text type="strong">{importType}</s-text>) : null} </s-table-cell>
                                            <s-table-cell><s-text type="strong">Unpublish</s-text></s-table-cell>
                                            <s-table-cell>{unpublish.startTime ? unpublish?.timeAgo : update?.timeAgo}</s-table-cell>
                                            <s-table-cell>{unpublish.total}</s-table-cell>
                                            {/* <s-table-cell><div style={{ width: '125px' }}>
                                        <ProgressBar progress={create.completed != 0 ? (create.completed / create.total) * 100 : 0} size="medium" />
                                    </div></s-table-cell> */}
                                            <s-table-cell>{unpublish.endTime ? FormatTimeDuration(unpublish?.startTime, unpublish?.endTime) : "_"}</s-table-cell>
                                            <s-table-cell>{(unpublish?.errors && unpublish?.errors?.length > 0) ?
                                                <ErrorLink
                                                    errorKey={bulkId + "-unpublish-err"}
                                                    count={unpublish?.errors?.length}
                                                    onClick={() => handleErrorClick(bulkId + "-unpublish-err", () => generateProductErrorModal(unpublish?.errors))}
                                                />
                                                : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>}
                                            </s-table-cell>
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>
                                                {status !== "canceled" ?
                                                    <s-badge tone={unpublish?.status === "completed" ? "success" : "info"}>{unpublish?.status}</s-badge>
                                                    : <s-badge tone="critical">{status}</s-badge>
                                                }
                                            </s-table-cell>
                                        </s-table-row> : null}



                                    {metaUpdate ?
                                        <s-table-row key={bulkId + "metaUpdate"}>
                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    <s-text type="strong">{update || create ? "" : '#' + id}</s-text>
                                                    {catalogUrl && !update ? (
                                                        <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                            <s-icon type="upload" tone='base' />
                                                        </s-link>
                                                    ) : null}
                                                </s-stack>                                    </s-table-cell>
                                            <s-table-cell> {!update && !create ? (<s-text type="strong">{importType}</s-text>) : null} </s-table-cell>
                                            <s-table-cell><s-text type="strong">Metafields</s-text></s-table-cell>
                                            <s-table-cell>{metaUpdate?.startTime ? metaUpdate?.timeAgo : update?.timeAgo}</s-table-cell>
                                            <s-table-cell>{metaUpdate?.total}</s-table-cell>
                                            {/* <s-table-cell><div style={{ width: '125px' }}>
                                        <ProgressBar progress={metaUpdate.completed != 0 ? (metaUpdate.completed / metaUpdate.total) * 100 : 0} size="medium" />
                                    </div></s-table-cell> */}
                                            <s-table-cell>{metaUpdate?.endTime ? FormatTimeDuration(metaUpdate?.startTime, metaUpdate?.endTime) : "_"}</s-table-cell>
                                            <s-table-cell>{(metaUpdate?.errors && metaUpdate?.errors?.length > 0) ?
                                                <ErrorLink
                                                    errorKey={bulkId + "-metaUpdate-err"}
                                                    count={metaUpdate?.errors?.length}
                                                    onClick={() => handleErrorClick(bulkId + "-metaUpdate-err", () => generateMetafieldErrorModal(metaUpdate?.errors))}
                                                />
                                                : <s-text tone="success">{status !== "canceled" ? "_" : "_"}</s-text>}
                                            </s-table-cell>
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>
                                                {status !== "canceled" ?
                                                    <s-badge tone={metaUpdate?.status === "completed" ? "success" : "info"}>{metaUpdate?.status}</s-badge>
                                                    : <s-badge tone="critical">{status}</s-badge>
                                                }
                                            </s-table-cell>
                                        </s-table-row> : null}

                                    {metaDelete ?
                                        <s-table-row key={bulkId + new Date()}>
                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    <s-text type="strong">{update ? "" : '#' + id}</s-text>
                                                    {catalogUrl && !update ? (
                                                        <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                            <s-icon type="upload" tone='base' />
                                                        </s-link>
                                                    ) : null}
                                                </s-stack>                                    </s-table-cell>
                                            <s-table-cell> {!update && !create ? (<s-text type="strong">{importType}</s-text>) : null} </s-table-cell>
                                            <s-table-cell><s-text type="strong">Delete metafields</s-text></s-table-cell>
                                            <s-table-cell>{metaDelete?.timeAgo}</s-table-cell>
                                            <s-table-cell>{metaDelete?.total}</s-table-cell>
                                            {/* <s-table-cell><div style={{ width: '125px' }}>
                                        <ProgressBar progress={metaDelete.completed != 0 ? (metaDelete.completed / metaDelete.total) * 100 : 0} size="medium" />
                                    </div></s-table-cell> */}
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>{"_"}
                                            </s-table-cell>
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>
                                                <s-badge tone={metaDelete?.completed === metaDelete?.total ? "success" : "info"}>{metaDelete?.completed === metaDelete?.total ? "completed" : "running"}</s-badge>
                                            </s-table-cell>
                                        </s-table-row> : null}

                                    {productDelete ?
                                        <s-table-row key={bulkId + new Date()}>
                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignment="center">
                                                    <s-text type="strong">{update ? "" : '#' + id}</s-text>
                                                    {catalogUrl && !update ? (
                                                        <s-link url={catalogUrl} download={catalogUrl.split('/').pop()}>
                                                            <s-icon type="upload" tone='base' />
                                                        </s-link>
                                                    ) : null}
                                                </s-stack>                                    </s-table-cell>
                                            <s-table-cell> {!update && !create ? (<s-text type="strong">{importType}</s-text>) : null} </s-table-cell>
                                            <s-table-cell><s-text type="strong">Delete products</s-text></s-table-cell>
                                            <s-table-cell>{productDelete?.timeAgo}</s-table-cell>
                                            <s-table-cell>{productDelete?.total}</s-table-cell>
                                            {/* <s-table-cell><div style={{ width: '125px' }}>
                                        <ProgressBar progress={productDelete.completed != 0 ? (productDelete.completed / productDelete.total) * 100 : 0} size="medium" />
                                    </div></s-table-cell> */}
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>{"_"}</s-table-cell>
                                            <s-table-cell>
                                                <s-badge tone={productDelete?.completed === productDelete?.total ? "success" : "info"}>{productDelete?.completed === productDelete?.total ? "completed" : "running"}</s-badge>
                                            </s-table-cell>
                                        </s-table-row> : null}
                                </>
                            ))
                    }
                </s-table-body>
            </s-table>
        </>
    )
}

